import React from 'react';
import Layout from './components/Layout';
import { Helmet } from 'react-helmet';
import SEO from './components/SEO';
import { Link } from 'react-router-dom';

interface Event {
  id: number;
  month: string;
  name: string;
  location: string;
  description: string;
  type: 'festival' | 'sports' | 'music' | 'fashion' | 'film' | 'awards' | 'series';
}

interface Partnership {
  id: number;
  name: string;
  description: string;
}

interface MultiDateSeries {
  id: number;
  name: string;
  locations: string;
  period: string;
  description: string;
}

const EventsRoadmap: React.FC = () => {
  // Events calendar from Aug 2025 to 2026
  const events: Event[] = [
    // August 2025
    {
      id: 1,
      month: 'AUG',
      name: 'Monterey Car Week',
      location: 'California',
      description: 'Exclusive automotive experiences and brand activations during the prestigious car showcase event.',
      type: 'festival'
    },
    {
      id: 2,
      month: 'AUG',
      name: 'Labyrinth on the Thames',
      location: 'London',
      description: 'Immersive riverside experience with interactive digital installations and performances.',
      type: 'music'
    },
    {
      id: 3,
      month: 'AUG',
      name: 'SHY FX Presents at Notting Hill Carnival',
      location: 'London',
      description: 'VIP sound system and digital experiences at London\'s biggest street festival.',
      type: 'music'
    },
    {
      id: 4,
      month: 'AUG',
      name: 'Wythenshawe Festival',
      location: 'Manchester',
      description: 'Community festival featuring interactive technology experiences and digital art displays.',
      type: 'festival'
    },
    {
      id: 5,
      month: 'AUG',
      name: 'UK B-Boy Championships',
      location: 'Manchester',
      description: 'Digital visualization and interactive elements for the breakdancing competition.',
      type: 'sports'
    },
    {
      id: 6,
      month: 'AUG',
      name: 'Venice Film Festival',
      location: 'Venice',
      description: 'Exclusive digital experiences and VR showcases for festival attendees and filmmakers.',
      type: 'film'
    },
    
    // September 2025
    {
      id: 7,
      month: 'SEPT',
      name: 'Gumball Rally',
      location: 'Istanbul > Ibiza',
      description: 'Digital route mapping and interactive experiences for rally participants and fans.',
      type: 'sports'
    },
    {
      id: 8,
      month: 'SEPT',
      name: 'Trick Tenerife',
      location: 'Tenerife',
      description: 'Extreme sports event with digital capture technology and real-time performance visualization.',
      type: 'sports'
    },
    {
      id: 9,
      month: 'SEPT',
      name: 'NFL International Series',
      location: 'Europe',
      description: 'Interactive fan experiences and digital activations around the American football games.',
      type: 'sports'
    },
    {
      id: 10,
      month: 'SEPT',
      name: 'Manchester Fashion Week',
      location: 'Manchester',
      description: 'Digital runway enhancements and virtual try-on experiences for fashion showcases.',
      type: 'fashion'
    },
    {
      id: 11,
      month: 'SEPT',
      name: 'Warehouse Project',
      location: 'Manchester',
      description: 'Immersive digital installations and projection mapping for the iconic music venue series.',
      type: 'music'
    },
    {
      id: 12,
      month: 'SEPT',
      name: 'Swim Week',
      location: 'Palm Beach',
      description: 'Digital experiences and virtual fitting technologies for swimwear fashion event.',
      type: 'fashion'
    },
    {
      id: 13.1,
      month: 'SEPT',
      name: 'Digital Fashion Week with Epic Games',
      location: 'London',
      description: 'Innovative digital fashion showcase featuring virtual runway shows, interactive exhibitions, and real-time 3D garment experiences powered by Epic Games technology.',
      type: 'fashion'
    },
    {
      id: 13.2,
      month: 'SEPT',
      name: 'Clips and Stogies Podcast',
      location: 'Los Angeles',
      description: 'Special episode featuring basketball legends Shaquille O\'Neal and James Harden with immersive digital audience experience and interactive elements.',
      type: 'series'
    },
    {
      id: 13.3,
      month: 'SEPT',
      name: 'Post Malone & Jelly Roll Concert',
      location: 'London Stadium',
      description: 'Groundbreaking stadium show featuring innovative digital activations, AR integrations, and immersive fan experiences complementing these genre-blending artists.',
      type: 'music'
    },
    {
      id: 13.4,
      month: 'SEPT',
      name: 'Lamborghini Opening',
      location: 'Mayfair, London (September 9th)',
      description: 'Exclusive launch event featuring digital experiences and interactive installations for the prestigious automotive brand\'s new Mayfair location.',
      type: 'festival'
    },
    
    // October 2025
    {
      id: 13,
      month: 'OCT',
      name: 'E1 Series',
      location: 'Lagos',
      description: 'Digital experiences for the electric raceboat championship series with sustainability focus.',
      type: 'sports'
    },
    {
      id: 13.5,
      month: 'OCT',
      name: 'AE Global Summit by Thinking About Thinking',
      location: 'Friends House, London',
      description: 'Premier AI and emerging tech summit featuring industry giants including NVIDIA, Google DeepMind, Meta, and more. Showcasing cutting-edge immersive experiences and future-focused discussions.',
      type: 'festival'
    },
    {
      id: 14,
      month: 'OCT',
      name: 'Café Mambo',
      location: 'Blackpool Tower',
      description: 'Transformative digital experiences bringing Ibiza vibes to the iconic Blackpool Tower.',
      type: 'music'
    },
    {
      id: 15,
      month: 'OCT',
      name: 'Athlos',
      location: 'New York',
      description: 'Digital fitness and wellness experiences for the athletic performance showcase.',
      type: 'sports'
    },
    {
      id: 16,
      month: 'OCT',
      name: 'Lord Mayor\'s Fire Fight Night',
      location: 'London (LDN v NYC)',
      description: 'Digital enhancements for the international boxing competition with city pride at stake.',
      type: 'sports'
    },
    {
      id: 17,
      month: 'OCT',
      name: 'United States F1',
      location: 'USA',
      description: 'Immersive fan experiences and digital activations around the Formula 1 Grand Prix.',
      type: 'sports'
    },
    
    // November 2025
    {
      id: 18,
      month: 'NOV',
      name: 'E1 Series - Miami Season Finale',
      location: 'Miami',
      description: 'Spectacular digital finale for the electric raceboat championship series.',
      type: 'sports'
    },
    {
      id: 19,
      month: 'NOV',
      name: 'National PA Awards',
      location: 'Manchester',
      description: 'Digital experiences and interactive elements for the prestigious awards ceremony.',
      type: 'awards'
    },
    {
      id: 20,
      month: 'NOV',
      name: 'Latin Grammys',
      location: 'Miami',
      description: 'Digital experiences and virtual elements for the Latin music awards show.',
      type: 'awards'
    },
    {
      id: 21,
      month: 'NOV',
      name: 'Las Vegas F1',
      location: 'Las Vegas',
      description: 'Spectacular digital activations and experiences around the night race in Vegas.',
      type: 'sports'
    },
    {
      id: 22,
      month: 'NOV',
      name: 'I Love MCR Awards',
      location: 'Manchester',
      description: 'Digital experiences for the celebration of Manchester\'s best and brightest.',
      type: 'awards'
    },
    {
      id: 23,
      month: 'NOV',
      name: 'Qatar F1',
      location: 'Qatar',
      description: 'Immersive experiences and digital activations for the Middle East Grand Prix.',
      type: 'sports'
    },
    
    // December 2025
    {
      id: 24,
      month: 'DEC',
      name: 'Abu Dhabi F1',
      location: 'Abu Dhabi',
      description: 'Season finale digital experiences and activations for the Formula 1 championship.',
      type: 'sports'
    },
    {
      id: 25,
      month: 'DEC',
      name: 'Café Mambo',
      location: 'Epic Studios Norwich',
      description: 'Bringing Ibiza vibes to Norwich with immersive digital experiences and performances.',
      type: 'music'
    },
    {
      id: 26,
      month: 'DEC',
      name: 'Art Basel',
      location: 'Miami',
      description: 'Digital art installations and NFT exhibitions at the prestigious art fair.',
      type: 'festival'
    },
    {
      id: 27,
      month: 'DEC',
      name: 'Sunburn Festival',
      location: 'Goa',
      description: 'Immersive digital experiences and installations for the electronic music festival.',
      type: 'music'
    },
    {
      id: 28,
      month: 'DEC',
      name: 'Paddy Power World Darts Championship',
      location: 'London',
      description: 'Digital enhancements and interactive fan experiences for the darts tournament.',
      type: 'sports'
    },
    
    // 2026
    {
      id: 29,
      month: '2026',
      name: 'Sundance Film Festival',
      location: 'Park City, Utah',
      description: 'Digital experiences and virtual reality showcases for the independent film festival.',
      type: 'film'
    },
    {
      id: 30,
      month: '2026',
      name: 'The Brits',
      location: 'London',
      description: 'Digital experiences and augmented performances for the British music awards.',
      type: 'awards'
    },
    {
      id: 31,
      month: '2026',
      name: 'The Grammys',
      location: 'Los Angeles',
      description: 'Innovative digital experiences for music\'s biggest night.',
      type: 'awards'
    },
    {
      id: 32,
      month: '2026',
      name: 'Superbowl Weekend',
      location: 'USA',
      description: 'Digital activations and immersive experiences for the biggest sporting event in America.',
      type: 'sports'
    },
    {
      id: 33,
      month: '2026',
      name: 'Cannes Film Festival',
      location: 'Cannes',
      description: 'Digital experiences and virtual showcases for the prestigious film festival.',
      type: 'film'
    },
    {
      id: 34,
      month: '2026',
      name: 'Coachella',
      location: 'California',
      description: 'Innovative digital experiences and installations for the iconic music festival.',
      type: 'music'
    },
    {
      id: 35,
      month: '2026',
      name: 'Soccer Aid',
      location: 'UK',
      description: 'Digital experiences and interactive elements for the charity football event.',
      type: 'sports'
    },
    {
      id: 35.5,
      month: '2026',
      name: 'NFC Conference',
      location: 'Lisbon',
      description: 'Leading blockchain and tech conference in June 2026 bringing together innovators, developers, and industry leaders to explore the future of digital ownership and decentralized technologies.',
      type: 'festival'
    },
    {
      id: 36,
      month: '2026',
      name: 'Copa Ibiza Relaunch',
      location: 'Ibiza',
      description: 'Digital experiences for the relaunch of the iconic Ibiza football tournament.',
      type: 'sports'
    },
    {
      id: 37,
      month: '2026',
      name: 'NBA Allstar Weekend',
      location: 'USA',
      description: 'Interactive digital experiences and activations for basketball\'s star-studded event.',
      type: 'sports'
    },
    {
      id: 38,
      month: '2026',
      name: 'Parklife',
      location: 'Manchester',
      description: 'Digital experiences and installations for the major UK music festival.',
      type: 'music'
    },
    {
      id: 39,
      month: '2026',
      name: 'Wimbledon',
      location: 'London',
      description: 'Digital experiences and interactive elements for the prestigious tennis tournament.',
      type: 'sports'
    },
    {
      id: 40,
      month: '2026',
      name: 'Soho House Festival',
      location: 'London',
      description: 'Exclusive digital experiences for the members-only summer event.',
      type: 'festival'
    },
    {
      id: 41,
      month: '2026',
      name: 'NFT Paris',
      location: 'Paris',
      description: 'Digital art and NFT exhibitions at the cutting-edge technology event.',
      type: 'festival'
    },
    {
      id: 42,
      month: '2026',
      name: 'The Oscars',
      location: 'Los Angeles',
      description: 'Digital experiences and virtual elements for film\'s biggest night.',
      type: 'awards'
    },
    {
      id: 43,
      month: '2026',
      name: 'SXSW',
      location: 'Austin, Texas',
      description: 'Innovative digital experiences and interactive installations at the intersection of tech, film, and music.',
      type: 'festival'
    }
  ];

  // Multi-date opportunities
  const multiDateSeries: MultiDateSeries[] = [
    {
      id: 1,
      name: 'DIPLO\'S RUN CLUB',
      locations: 'San Fran / Seattle / LA / NYC / San Diego / Miami / Phoenix',
      period: 'September 2025 - January 2026',
      description: 'Digital experiences and interactive elements for Diplo\'s running club series across major US cities.'
    },
    {
      id: 2,
      name: 'NOPRBLM SECRET DJ POP UP SERIES',
      locations: 'Manchester',
      period: 'August / Sept / October / November / December',
      description: 'Exclusive secret location pop-up events featuring digital experiences and top DJ performances.'
    },
    {
      id: 3,
      name: 'CAFÉ MAMBO UK SERIES',
      locations: 'Iconic indoor & outdoor venues across the UK',
      period: 'Winter 2025 & Summer 2026',
      description: 'Bringing Ibiza vibes to the UK with immersive digital experiences and performances at iconic venues.'
    }
  ];

  // Ongoing partnership opportunities
  const partnerships: Partnership[] = [
    {
      id: 1,
      name: 'PREMIER LEAGUE',
      description: '25/26 & 26/27 SEASONS'
    },
    {
      id: 2,
      name: 'WOMEN\'S SUPER LEAGUE',
      description: '25/26 & 26/27 SEASONS'
    },
    {
      id: 3,
      name: 'MUSIC VIDEO PRODUCT PLACEMENT',
      description: 'Digital product placement and AR experiences in high-profile music videos.'
    },
    {
      id: 4,
      name: 'FILM & TV PRODUCT PLACEMENT',
      description: 'Strategic digital product integration in major film and television productions.'
    },
    {
      id: 5,
      name: 'ESPORTS PARTNERSHIPS',
      description: 'Digital experiences and brand activations in competitive gaming events and leagues.'
    },
    {
      id: 6,
      name: 'CELEBRITY BRAND AMBASSADORS',
      description: 'Strategic partnerships with influential celebrities for brand representation.'
    },
    {
      id: 7,
      name: 'CELEBRITY PODCAST PARTNERSHIPS',
      description: 'Digital sponsorships and creative integrations with popular celebrity podcasts.'
    },
    {
      id: 8,
      name: 'FORMULA 1 - 2026 PARTNERSHIPS',
      description: 'Strategic sponsorships and digital experiences for the 2026 Formula 1 season.'
    },
    {
      id: 9,
      name: 'E1 SEASON 3 - 2026 PARTNERSHIPS',
      description: 'Digital innovations and experiences for the electric raceboat championship series.'
    },
    {
      id: 10,
      name: 'SAUDI PRO LEAGUE',
      description: '25/26 & 26/27 SEASONS'
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Events Roadmap | NOPRBLM"
        description="Exclusive preview of upcoming NOPRBLM activations and experiences across music, sports, fashion, and culture."
        keywords="NOPRBLM, events, roadmap, activations, experiences, music, sports, fashion, culture"
        canonicalUrl="/events-roadmap"
      />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <section className="hero hero-simple">
        <h1>Events Roadmap</h1>
        <p>Exclusive preview of upcoming NOPRBLM activations and experiences across music, sports, fashion, and culture.</p>
      </section>
      
      <section className="section">
        {['AUG', 'SEPT', 'OCT', 'NOV', 'DEC', '2026'].map((month) => (
          <div className="event-month-section" key={month}>
            <h2 className="event-month-title">{month}</h2>
            <div className="events-grid">
              {events
                .filter(event => event.month === month)
                .map((event) => (
                  <div 
                    className={`event-card event-${event.type}`} 
                    key={event.id}
                  >
                    <div className="event-content">
                      <h3 className="event-title">{event.name}</h3>
                      <div className="event-location">{event.location}</div>
                      <div className="event-description">{event.description}</div>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        ))}

        <div className="event-categories">
          <div className="event-category">
            <span className="category-marker festival"></span>
            <span className="category-name">Festival</span>
          </div>
          <div className="event-category">
            <span className="category-marker sports"></span>
            <span className="category-name">Sports</span>
          </div>
          <div className="event-category">
            <span className="category-marker music"></span>
            <span className="category-name">Music</span>
          </div>
          <div className="event-category">
            <span className="category-marker fashion"></span>
            <span className="category-name">Fashion</span>
          </div>
          <div className="event-category">
            <span className="category-marker film"></span>
            <span className="category-name">Film</span>
          </div>
          <div className="event-category">
            <span className="category-marker awards"></span>
            <span className="category-name">Awards</span>
          </div>
        </div>
      </section>

      <section className="section multi-date-section">
        <h2 className="section-title">Multi-Date Opportunities</h2>
        <div className="multi-date-grid">
          {multiDateSeries.map((series) => (
            <div className="multi-date-card" key={series.id}>
              <h3 className="multi-date-title">{series.name}</h3>
              <div className="multi-date-locations">{series.locations}</div>
              <div className="multi-date-period">{series.period}</div>
              <p className="multi-date-description">{series.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section partnership-section">
        <h2 className="section-title">Ongoing Partnership Opportunities</h2>
        <div className="partnerships-grid">
          {partnerships.map((partnership) => (
            <div className="partnership-card" key={partnership.id}>
              <h3 className="partnership-title">{partnership.name}</h3>
              <div className="partnership-description">{partnership.description}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section contact-section">
        <h2 className="section-title">Interested in Collaboration?</h2>
        <p className="section-subtitle">
          For more information on any of these opportunities or to discuss custom experiences for your brand, get in touch with our partnerships team.
        </p>
        <div className="cta-center">
          <Link to="/contact" className="btn-primary">Contact Us</Link>
        </div>
      </section>
    </Layout>
  );
};

export default EventsRoadmap;